import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/layout/Layout';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEmpresa } from '@/contexts/EmpresaContext';
import { useImageUpload } from '@/hooks/useImageUpload';

const AVATAR_STORAGE_KEY = 'admin_avatar_url';

export default function PerfilPage() {
  const { session, isAuthenticated } = useAdminAuth();
  const { empresaSelecionada } = useEmpresa();

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [nome, setNome] = useState(session.nome || '');
  const [email, setEmail] = useState(session.email || '');
  const [telefone, setTelefone] = useState('(11) 98765-4321');
  const [cargo, setCargo] = useState(session.role || '');
  const [editMode, setEditMode] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);

  // Estados para confirmação por token
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState('');
  const [tokenSent, setTokenSent] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{ nome?: string; email?: string; telefone?: string } | null>(null);
  const [countdown, setCountdown] = useState(0);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(() => {
    return session.avatar || localStorage.getItem(AVATAR_STORAGE_KEY) || null;
  });

  const {
    previewUrl: avatarPreview,
    uploadedUrl: newAvatarUrl,
    isUploading: isAvatarUploading,
    error: avatarError,
    fileInputRef: avatarFileRef,
    handleFileSelect: handleAvatarSelect,
    triggerFileInput: triggerAvatarInput,
    clearImage: clearAvatar,
    resetError: resetAvatarError,
  } = useImageUpload({ bucket: 'avatars', prefix: `user-${session.userId || 'unknown'}-`, maxSizeMB: 3 });

  const hiddenAvatarInputRef = useRef<HTMLInputElement | null>(null);

  // Countdown para reenvio do token
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Atualiza avatar quando upload completa
  useEffect(() => {
    if (newAvatarUrl) {
      setAvatarUrl(newAvatarUrl);
      localStorage.setItem(AVATAR_STORAGE_KEY, newAvatarUrl);
    }
  }, [newAvatarUrl]);

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSalvar = () => {
    if (!nome.trim()) {
      mostrarToast('Nome é obrigatório', 'error');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      mostrarToast('Email inválido', 'error');
      return;
    }

    const emailChanged = email !== session.email;
    const telefoneChanged = telefone !== '(11) 98765-4321';

    if (emailChanged || telefoneChanged) {
      // Precisa de confirmação por token
      setPendingChanges({ nome, email, telefone });
      setTokenModalOpen(true);
      setTokenValue('');
      setTokenSent(false);
      setTokenError('');
      setTokenLoading(false);
      setCountdown(0);
    } else {
      // Sem alteração em email/telefone, salva direto
      mostrarToast('Perfil atualizado com sucesso', 'success');
      setEditMode(false);
    }
  };

  const handleEnviarToken = () => {
    setTokenLoading(true);
    setTokenError('');
    // Simula envio de token para email e telefone atuais
    setTimeout(() => {
      setTokenSent(true);
      setTokenLoading(false);
      setCountdown(60);
      mostrarToast('Token enviado para seu email e telefone atuais', 'success');
    }, 1500);
  };

  const handleValidarToken = () => {
    if (!tokenValue || tokenValue.length < 6) {
      setTokenError('Digite o token de 6 dígitos');
      return;
    }
    // Simula validação: token demo é "123456" ou qualquer 6 dígitos numéricos
    if (!/^\d{6}$/.test(tokenValue)) {
      setTokenError('Token inválido. Digite os 6 dígitos numéricos');
      return;
    }
    // Token aceito (simulação)
    setTokenModalOpen(false);
    setTokenValue('');
    setTokenSent(false);
    setTokenError('');
    setPendingChanges(null);
    mostrarToast('Contato verificado com sucesso. Perfil atualizado!', 'success');
    setEditMode(false);
  };

  const handleCancelarToken = () => {
    setTokenModalOpen(false);
    setTokenValue('');
    setTokenSent(false);
    setTokenError('');
    setTokenLoading(false);
    setPendingChanges(null);
    // Reverte email e telefone para os valores originais
    setEmail(session.email || '');
    setTelefone('(11) 98765-4321');
    mostrarToast('Alteração de contato cancelada', 'error');
  };

  const handleAlterarSenha = () => {
    if (!novaSenha || novaSenha.length < 8) {
      mostrarToast('Nova senha deve ter pelo menos 8 caracteres', 'error');
      return;
    }
    const forca = calcularForcaSenha(novaSenha);
    if (forca.score < 3) {
      mostrarToast('A senha não atende aos critérios de segurança mínimos', 'error');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      mostrarToast('As senhas não conferem', 'error');
      return;
    }
    mostrarToast('Senha alterada com sucesso', 'success');
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setShowSenha(false);
  };

  const calcularForcaSenha = (senha: string) => {
    const criterios = [
      { label: 'Mínimo 8 caracteres', met: senha.length >= 8 },
      { label: 'Letra maiúscula (A-Z)', met: /[A-Z]/.test(senha) },
      { label: 'Letra minúscula (a-z)', met: /[a-z]/.test(senha) },
      { label: 'Número (0-9)', met: /\d/.test(senha) },
      { label: 'Caractere especial (!@#$...)', met: /[^A-Za-z0-9]/.test(senha) },
    ];
    const score = criterios.filter((c) => c.met).length;
    return { score, criterios, total: criterios.length };
  };

  const getForcaLabel = (score: number) => {
    if (score <= 1) return { label: 'Muito fraca', color: 'bg-red-500', text: 'text-red-600' };
    if (score === 2) return { label: 'Fraca', color: 'bg-orange-500', text: 'text-orange-600' };
    if (score === 3) return { label: 'Média', color: 'bg-yellow-500', text: 'text-yellow-600' };
    if (score === 4) return { label: 'Forte', color: 'bg-emerald-500', text: 'text-emerald-600' };
    return { label: 'Muito forte', color: 'bg-emerald-600', text: 'text-emerald-700' };
  };

  const handleRemoverFoto = () => {
    setAvatarUrl(null);
    localStorage.removeItem(AVATAR_STORAGE_KEY);
    clearAvatar();
    mostrarToast('Foto removida', 'success');
  };

  const getRoleLabel = (role?: string) => {
    const map: Record<string, string> = {
      admin_empresa: 'Administrador',
      gerente: 'Gerente',
      operador: 'Operador',
      visualizador: 'Visualizador',
    };
    return map[role || ''] || role || 'Usuário';
  };

  if (!isAuthenticated) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
            <p className="text-sm text-gray-500 mt-1">
              Gerencie suas informações pessoais e segurança
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-building-line text-gray-400"></i>
            </div>
            <span className="font-medium text-gray-700">{empresaSelecionada.nome}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Card — Avatar e Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="text-center">
              <div className="relative mx-auto inline-block group">
                {/* Avatar image ou fallback */}
                {avatarPreview || avatarUrl ? (
                  <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-orange-100 mx-auto relative">
                    <img
                      src={avatarPreview || avatarUrl || ''}
                      alt="Foto de perfil"
                      className="w-full h-full object-cover"
                    />
                    {isAvatarUploading && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center mx-auto cursor-pointer hover:from-orange-500 hover:to-amber-600 transition-all duration-200"
                    onClick={triggerAvatarInput}
                  >
                    <span className="text-white font-bold text-3xl">
                      {nome ? nome.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                )}

                {/* Botão de upload overlay */}
                <button
                  onClick={triggerAvatarInput}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-orange-50 hover:border-orange-300 transition-all cursor-pointer"
                  title="Alterar foto"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-camera-line text-gray-500 text-xs"></i>
                  </div>
                </button>

                {/* Hidden file inputs */}
                <input
                  ref={(el) => {
                    hiddenAvatarInputRef.current = el;
                    if (avatarFileRef) {
                      (avatarFileRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                    }
                  }}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAvatarSelect(file);
                  }}
                  className="hidden"
                />
              </div>

              {/* Erro de upload */}
              {avatarError && (
                <p className="text-xs text-red-500 mt-2">{avatarError}</p>
              )}

              {/* Remover foto */}
              {(avatarPreview || avatarUrl) && !isAvatarUploading && (
                <button
                  onClick={handleRemoverFoto}
                  className="mt-2 text-xs text-red-500 hover:text-red-600 font-medium cursor-pointer transition-colors"
                >
                  Remover foto
                </button>
              )}

              <h3 className="text-lg font-bold text-gray-900 mt-3">{nome || 'Usuário'}</h3>
              <span className="inline-block mt-1 text-xs font-bold bg-orange-100 text-orange-700 px-3 py-1 rounded-full">
                {getRoleLabel(cargo)}
              </span>
              <p className="text-xs text-gray-500 mt-2">{email}</p>
              <p className="text-xs text-gray-400 mt-0.5">{empresaSelecionada.nome}</p>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Membro desde</span>
                <span className="font-medium text-gray-800">{new Date().toLocaleDateString('pt-BR')}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Último login</span>
                <span className="font-medium text-gray-800">Hoje, {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <span className="inline-flex items-center text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5"></div>
                  Ativo
                </span>
              </div>
            </div>
          </div>

          {/* Card — Dados Pessoais */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-bold text-gray-900 flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-orange-500"></i>
                  </div>
                  <span>Dados Pessoais</span>
                </h3>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={editMode ? 'ri-close-line' : 'ri-edit-line'}></i>
                  </div>
                  <span>{editMode ? 'Cancelar' : 'Editar'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Nome completo</label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    disabled={!editMode}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm text-gray-800 transition-all ${
                      editMode
                        ? 'border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500'
                        : 'border-gray-100 bg-gray-50 text-gray-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Email
                    {editMode && email !== session.email && (
                      <span className="text-orange-600 ml-1">(alteração requer token)</span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!editMode}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm text-gray-800 transition-all ${
                      editMode
                        ? 'border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500'
                        : 'border-gray-100 bg-gray-50 text-gray-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    Telefone
                    {editMode && telefone !== '(11) 98765-4321' && (
                      <span className="text-orange-600 ml-1">(alteração requer token)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    disabled={!editMode}
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm text-gray-800 transition-all ${
                      editMode
                        ? 'border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500'
                        : 'border-gray-100 bg-gray-50 text-gray-500'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Cargo</label>
                  <input
                    type="text"
                    value={getRoleLabel(cargo)}
                    disabled
                    className="w-full px-3 py-2.5 border border-gray-100 bg-gray-50 rounded-lg text-sm text-gray-500"
                  />
                </div>
              </div>

              {editMode && (
                <div className="flex justify-end mt-5">
                  <button
                    onClick={handleSalvar}
                    className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md"
                  >
                    Salvar alterações
                  </button>
                </div>
              )}
            </div>

            {/* Card — Alterar Senha */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-base font-bold text-gray-900 flex items-center space-x-2 mb-5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-orange-500"></i>
                </div>
                <span>Segurança</span>
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Senha atual</label>
                    <input
                      type="password"
                      value={senhaAtual}
                      onChange={(e) => setSenhaAtual(e.target.value)}
                      placeholder="••••••"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Nova senha</label>
                    <input
                      type="password"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Confirmar senha</label>
                    <input
                      type="password"
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      placeholder="••••••"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                    />
                  </div>
                </div>

                {/* Barra de força da senha */}
                {novaSenha && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-gray-700">Força da senha</span>
                        <span className={`text-xs font-bold ${getForcaLabel(calcularForcaSenha(novaSenha).score).text}`}>
                          {getForcaLabel(calcularForcaSenha(novaSenha).score).label}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${getForcaLabel(calcularForcaSenha(novaSenha).score).color}`}
                          style={{ width: `${(calcularForcaSenha(novaSenha).score / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {calcularForcaSenha(novaSenha).criterios.map((criterio, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <div className={`w-4 h-4 flex items-center justify-center rounded-full ${criterio.met ? 'bg-emerald-100' : 'bg-gray-200'}`}>
                            <i className={`ri-check-line text-xs ${criterio.met ? 'text-emerald-600' : 'text-gray-400'}`}></i>
                          </div>
                          <span className={`text-xs ${criterio.met ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                            {criterio.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={handleAlterarSenha}
                    className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md"
                  >
                    Alterar senha
                  </button>
                </div>
              </div>
            </div>

            {/* Card — Sessões Ativas */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-base font-bold text-gray-900 flex items-center space-x-2 mb-5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-device-line text-orange-500"></i>
                </div>
                <span>Sessões Ativas</span>
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-computer-line text-emerald-600 text-sm"></i>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">Web — Chrome em Windows</p>
                      <p className="text-xs text-gray-500">São Paulo, Brasil • IP 192.168.1.1</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full">
                    Atual
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-smartphone-line text-gray-500 text-sm"></i>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">Mobile — iPhone Safari</p>
                      <p className="text-xs text-gray-500">São Paulo, Brasil • IP 189.34.12.7</p>
                    </div>
                  </div>
                  <button className="text-xs font-bold text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
                    Encerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal — Confirmação por Token */}
        {tokenModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={handleCancelarToken}></div>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Verificar identidade</h3>
                <button
                  onClick={handleCancelarToken}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className="ri-shield-check-line text-orange-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">Confirmação necessária</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Você está alterando seu email ou telefone. Para proteger sua conta, enviaremos um token de verificação para os seus contatos atuais:
                      </p>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-700 font-medium">
                          <i className="ri-mail-line text-gray-400 mr-1"></i>
                          {session.email || 'email@cadastro.com'}
                        </p>
                        <p className="text-xs text-gray-700 font-medium">
                          <i className="ri-phone-line text-gray-400 mr-1"></i>
                          (11) 98765-4321
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {!tokenSent ? (
                  <div className="text-center py-2">
                    <button
                      onClick={handleEnviarToken}
                      disabled={tokenLoading}
                      className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center space-x-2"
                    >
                      {tokenLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Enviando token...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          <span>Enviar token de verificação</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">
                        Token de verificação (6 dígitos)
                      </label>
                      <input
                        type="text"
                        value={tokenValue}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setTokenValue(val);
                          setTokenError('');
                        }}
                        placeholder="000000"
                        maxLength={6}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 text-center tracking-[0.5em] font-bold placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 transition-all"
                      />
                      {tokenError && (
                        <p className="text-xs text-red-500 mt-1.5">{tokenError}</p>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleEnviarToken}
                        disabled={countdown > 0 || tokenLoading}
                        className="text-xs font-bold text-orange-600 hover:text-orange-700 disabled:text-gray-400 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        {countdown > 0 ? `Reenviar em ${countdown}s` : 'Reenviar token'}
                      </button>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button
                        onClick={handleCancelarToken}
                        className="flex-1 py-2.5 border border-gray-200 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleValidarToken}
                        className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap"
                      >
                        Verificar e salvar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 transition-all ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={toast.type === 'success' ? 'ri-check-line text-sm' : 'ri-error-warning-line text-sm'}></i>
            </div>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </Layout>
  );
}